import React, { useState, useEffect } from 'react'
import {
  Form,
  Button,
  Modal,
  Container,
  Col,
  Row,
  Card} from 'react-bootstrap'
import 'bootstrap/dist/css/bootstrap.min.css'
import { BsPersonPlusFill, BsFunnelFill, BsSearch, BsArrowClockwise, BsPencilFill } from 'react-icons/bs'
import { FaUniversity } from 'react-icons/fa'
import { LuGraduationCap } from 'react-icons/lu'
import ApiEndpoints, {BASE_URL} from './commonComponents/ApiEndpoints'
import toast from 'react-hot-toast'
import EditInstituteModal from './EditInstituteModal'
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table'
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import TablePagination from './commonComponents/TablePagination'
import { useInstitute } from './commonComponents/InstituteContext'
import { useNavigate } from 'react-router-dom'

const ManageInstitute = () => {
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const navigate = useNavigate()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo, name, id } = useInstitute();
  const [showFilterCard, setShowFilterCard] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedInstitute, setSelectedInstitute] = useState(null)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const initialData = []

  const [data, setData] = useState(initialData)
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [showModal, setShowModal] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    ApiEndpoints.getAllInstitutes()
      .then((response) => {
        console.log(response.data.data)
        setData(response.data.data)
      })
      .catch((error) => {
        toast.error('Failed to access data.')
      })
  }, [loading])
  const [newInstitute, setNewInstitute] = useState({
    name: '',
    address: '',
    pincode: ''})
  const [logoFile, setLogoFile] = useState(null)

  // Filter Data Based on Search
  const filteredData = data.filter((item) =>
    Object.values(item).some((value) =>
      value !== null && value !== undefined
        ? value.toString().toLowerCase().includes(search.trim().toLowerCase())
        : false,
    ),
  )

  // Pagination Logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage)
  useEffect(() => {
    if (currentPage > totalPages && totalPages !== 0) {
      setCurrentPage(1)
    }
  }, [filteredData, totalPages])

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem)
  const highlightText = (text, search) => {
    if (!search) return text

    const lowerText = text.toString().toLowerCase()
    const lowerSearch = search.toLowerCase()

    // Check if all characters from search exist in the text
    const allCharsMatch = [...lowerSearch].every((char) =>
      lowerText.includes(char),
    )

    if (!allCharsMatch) {
      // Return plain text if any character is missing
      return text
    }

    // Highlight matching characters
    return text
      .toString()
      .split('')
      .map((char, i) => {
        return lowerSearch.includes(char.toLowerCase()) ? (
          <span key={i} style={{ backgroundColor: 'yellow', color: 'black' }}>
            {char}
          </span>
        ) : (
          <span key={i}>{char}</span>
        )
      })
  }

  // Handle Form Input
  const handleInputChange = (e) => {
    const { name, value } = e.target
    console.log(
      `Input Changed - Name: ${name}, Value: ${value} (Type: ${typeof value})`,
    )
    setNewInstitute((prev) => ({ ...prev, [name]: value }))
  }

  const resolveNewInstituteId = (response) => {
    const payload = response?.data?.data ?? response?.data
    if (!payload || typeof payload !== 'object') return null
    return payload.institute_id ?? payload.id ?? null
  }

  const handleSave = async (e) => {
    e.preventDefault()
    try {
      setLoading(true)
      const institutePayload = {
        name: newInstitute.name,
        address: newInstitute.address,
        pincode: newInstitute.pincode}
      if (logoFile && logoFile.size > 2 * 1024 * 1024) {
        toast.error('Logo image size should not exceed 2MB.')
        return
      }
      console.log('Sending data: ', institutePayload)
      const response = await ApiEndpoints.addInstitute(institutePayload)
      const instituteId = resolveNewInstituteId(response)

      if (logoFile) {
        if (!instituteId) {
          toast.error(
            'Institute was created but logo could not be linked (missing ID).',
          )
        } else {
          try {
            const formData = new FormData()
            formData.append('image', logoFile)
            const logoResponse = await ApiEndpoints.uploadInstituteLogo(
              instituteId,
              formData,
            )
            const logoUrl =
              logoResponse.data?.url ?? logoResponse.data?.data?.url ?? null
            if (!logoUrl) {
              toast.error(
                'Institute saved, but logo URL was not returned from the server.',
              )
            } else {
              await ApiEndpoints.editInstitute(instituteId, {
                ...institutePayload,
                logo: logoUrl})
            }
          } catch (logoErr) {
            toast.error(
              logoErr.response?.data?.message ||
                logoErr.message ||
                'Institute saved, but logo upload failed.',
            )
          }
        }
      }

      setNewInstitute({
        name: '',
        address: '',
        pincode: ''})
      setLogoFile(null)

      toast.success(response.data.message)
      setShowModal(false)
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.response?.message ||
          'Something went wrong.',
      )
      setShowModal(false)
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setSelectedInstitute({})
    setData(data)
    setSearch('')
    setCurrentPage(1)
  }

  const closeAddModal = () => {
    setShowModal(false)
    setLogoFile(null)
  }

  return (
    <>
      <style>
        {`
          .table td {
            user-select: text;
          }
          .table tbody tr {
            cursor: default;
          }
        `}
      </style>
      <div 
        style={{ 
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px'}}
      >
        {/* Header */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px'}}
        >
        <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
          <div 
            className="d-flex justify-content-between align-items-center"
            style={{
              background: '#F3F4F6',
              paddingLeft: '28px',
              paddingRight: '28px',
              paddingTop: '12px',
              paddingBottom: '12px',
              overflow: 'visible',
              borderBottom: '1px solid #D1D3D4',
              width: '100%'}}
          >
            {/* Left: Title and Add Institute Icon Button */}
            <div className="d-flex align-items-center gap-3">
              {/* School Logo */}
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                overflow: 'hidden', 
                background: '#223843',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexShrink: 0}}>
                {!logoError ? (
                  <img 
                    src={logo} 
                    alt="School Logo" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                )}
              </div>
              
              <h3 
                className="m-0 fw-bold" 
                style={{ 
                  letterSpacing: '-0.02em',
                  overflow: 'visible',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 'clamp(18px, 2.5vw, 22px)',
                  color: '#223843',
                  lineHeight: '1.2',
                  fontFamily: 'Inter, system-ui, sans-serif'}}
              >
                Manage Institutes
              </h3>
              {/* <button
                onClick={() => setShowModal(true)}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#223843',
                  border: 'none',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flexShrink: 0}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#223843'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title="Add New Institute"
              >
                <FaUniversity size={18} />
              </button> */}
            </div>
            
            {/* Right: Profile Icon with Dropdown */}
            {!isMobile && (
              <ProfileDropdown logout={logout} />
            )}
          </div>
        </div>
        </div>
        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px'}}
        >
          {/* Filter Toggle Button and Items Per Page */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              {/* Items Per Page Selector */}
              <div 
                className="d-flex align-items-center"
                style={{
                  backgroundColor: '#ffffff',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  gap: '8px',
                  border: '1px solid #D1D3D4'}}
              >
                <span style={{ color: '#6b7280', fontSize: '13px', whiteSpace: 'nowrap', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Show:
                </span>
                <Form.Select
                  className="w-auto"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value))
                    setCurrentPage(1)
                  }}
                  style={{
                    borderRadius: '6px',
                    border: '1px solid #D1D3D4',
                    backgroundColor: '#ffffff',
                    color: '#111827',
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '4px 32px 4px 12px',
                    minWidth: '140px',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    height: '36px',
                    fontFamily: 'Inter, system-ui, sans-serif'}}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#223843'
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#D1D3D4'
                  }}
                >
                  <option value="10">10 per page</option>
                  <option value="20">20 per page</option>
                  <option value="30">30 per page</option>
                </Form.Select>
              </div>
              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.color = '#ffffff'
                  e.currentTarget.style.borderColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#ffffff'
                  e.currentTarget.style.color = showFilterCard ? '#ffffff' : '#223843'
                  e.currentTarget.style.borderColor = '#D1D3D4'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div>

            {/* Add New — desktop (mobile below, match CreateAnnouncement.js) */}
              <button
                type="button"
                className="d-none d-md-flex align-items-center justify-content-center"
                onClick={() => {
                  navigate('/AddInstitute/New')
                }}
                style={{
                  borderRadius: '10px',
                  padding: '6px 10px',
                  height: '44px',
                  border: 'none',
                  backgroundColor: '#223843',
                  color: '#ffffff',
                  gap: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#1f323a'
                  e.currentTarget.style.transform = 'scale(1.03)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#223843'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title="Add New Institute"
              >
                <FaUniversity size={18} />
                <span style={{ fontSize: '12px', fontWeight: '650', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Add New Institute
                </span>
              </button>
          </div>

          {/* <div className="d-md-none d-flex justify-content-end mb-4">
            <button
              type="button"
              onClick={() => {
                navigate('/AddInstitute/New')
              }}
              style={{
                borderRadius: '10px',
                padding: '6px 10px',
                height: '44px',
                border: 'none',
                backgroundColor: '#223843',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'}}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1f323a'
                e.currentTarget.style.transform = 'scale(1.03)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#223843'
                e.currentTarget.style.transform = 'scale(1)'
              }}
              title="Add New Institute"
            >
              <FaUniversity size={18} />
              <span style={{ fontSize: '12px', fontWeight: '650', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Add New Institute
              </span>
            </button>
          </div> */}

          {/* Modern Filter Section */}
          <div 
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0'}}
          >
            {showFilterCard && (
              <Card 
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'hidden'}}
              >
                {/* Header */}
                <div 
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6'}}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div 
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'}}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5 className="m-0 fw-semibold text-start" style={{ color: '#111827', fontSize: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Search Institutes
                        </h5>
                        <p className="m-0 mt-1" style={{fontSize: '13px', color: '#6b7280' }}>
                          Find institutes quickly by name, address, or pincode
                        </p>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        onClick={handleRefresh}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#10B981',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'}}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#059669'
                          e.currentTarget.style.transform = 'scale(1.05)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#10B981'
                          e.currentTarget.style.transform = 'scale(1)'
                        }}
                        title="Refresh"
                      >
                        <BsArrowClockwise size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <Row className="g-3">
                    <Col xs={12}>
                      <div style={{ position: 'relative' }}>
                        <label className="fw-semibold text-start mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Search Institute Name
                        </label>
                        <div style={{ position: 'relative' }}>
                          <BsSearch 
                            style={{ 
                              position: 'absolute', 
                              left: '12px', 
                              top: '50%', 
                              transform: 'translateY(-50%)',
                              color: '#6b7280',
                              fontSize: '16px',
                              zIndex: 1}} 
                          />
                          <Form.Control
                            type="text"
                            placeholder="Enter institute name..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                              paddingLeft: '40px',
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              transition: 'all 0.2s ease',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif'}}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#223843'
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#D1D3D4'
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>

        {/* Table */}
        <Card style={{ border: '1px solid #D1D3D4', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
          <Table className="table mb-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
            <Thead>
              <Tr style={{ backgroundColor: '#F3F4F6' }}>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Institute Name</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Address</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Pincode</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'center', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {currentItems.length > 0 ? (
                currentItems.map((item, index) => (
                  <Tr 
                    key={index}
                    style={{ 
                      backgroundColor: index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                      transition: 'background-color 0.2s ease',
                      borderBottom: '1px solid #E5E7EB'}}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(37, 99, 235, 0.06)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)'
                    }}
                  >
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{highlightText(item.name, search)}</Td>
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.address}</Td>
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.pincode}</Td>
                    <Td className="text-center" style={{ padding: '16px', border: 'none' }}>
                      {/* Desktop View - Circular Button */}
                      <div className="d-none d-md-block">
                        <button
                          onClick={() => {
                            setSelectedInstitute(item)
                            setShowEditModal(true)
                          }}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            backgroundColor: '#10B981',
                            border: 'none',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer'}}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#059669'
                            e.currentTarget.style.transform = 'scale(1.1)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#10B981'
                            e.currentTarget.style.transform = 'scale(1)'
                          }}
                          title="Edit Institute"
                        >
                          <BsPencilFill size={16} />
                        </button>
                      </div>
                      {/* Mobile View - Circular Button */}
                      <div className="d-md-none d-flex justify-content-center">
                        <button
                          onClick={() => {
                            setSelectedInstitute(item)
                            setShowEditModal(true)
                          }}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            backgroundColor: '#10B981',
                            border: 'none',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer'}}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#059669'
                            e.currentTarget.style.transform = 'scale(1.1)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#10B981'
                            e.currentTarget.style.transform = 'scale(1)'
                          }}
                          title="Edit Institute"
                        >
                          <BsPencilFill size={16} />
                        </button>
                      </div>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan="4" className="text-center" style={{ padding: '40px', fontSize: '14px', color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    No records found
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Card>


        {/* Pagination & Total Records */}


                <TablePagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
          totalItems={filteredData.length}
          itemLabel="institutes"
        />
        </div>

      {/* Add New Class Modal */}
      <Modal
        size="lg"
        show={showModal}
        onHide={closeAddModal}
        centered
      >
        <Modal.Header closeButton 
        closeVariant="white"
        className="text-white" style={{backgroundColor: '#223843'}}>
          <Modal.Title>Add New Institute</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSave}>
            {/* Class Name Field */}
            <Form.Group className="mb-3">
              <Form.Label>Institute Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={newInstitute.name}
                onChange={handleInputChange}
                placeholder="Enter Institute Name"
                className="p-2 border rounded"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="textarea"
                name="address"
                value={newInstitute.address}
                onChange={handleInputChange}
                placeholder="Enter Address"
                className="p-2 border rounded"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Pincode</Form.Label>
              <Form.Control
                type="number"
                name="pincode"
                value={newInstitute.pincode}
                onChange={handleInputChange}
                placeholder="Enter Pincode"
                className="p-2 border rounded"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Institute logo (optional)</Form.Label>
              <div className="d-flex align-items-center gap-3 mt-2 flex-wrap">
                <Form.Control
                  type="file"
                  accept="image/png, image/jpeg, image/jpg, image/webp"
                  onChange={(e) =>
                    setLogoFile(e.target.files?.[0] ?? null)
                  }
                  className="p-2 border rounded"
                />
                {logoFile && (
                  <img
                    src={URL.createObjectURL(logoFile)}
                    alt="Logo preview"
                    style={{
                      width: 72,
                      height: 72,
                      objectFit: 'cover',
                      borderRadius: 8,
                      border: '1px solid #ccc'}}
                  />
                )}
              </div>
              <Form.Text className="text-muted">
                Max 2MB. Uploaded after the institute is created.
              </Form.Text>
            </Form.Group>

            {/* <Form.Group className="mb-3">
              <Form.Label>Course</Form.Label>
              <Form.Select
                name="course_id"
                value={newClass.course_id}
                onChange={handleInputChange}
              >
                <option value="">Select Course</option>
                {institutes.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group> */}

            {/* Teacher Selection */}
            {/* <Form.Group className="mb-3">
              <Form.Label >Teacher</Form.Label>
              <Form.Select
                name="teacher"
                value={newClass.teacher}
                onChange={handleInputChange}
                className="p-2 border rounded"
              >
                <option value="Prem Sir">Prem Sir</option>
                <option value="Lucky Sir">Lucky Sir</option>
                <option value="Ambika Sir">Ambika Sir</option>
                <option value="Avinash Sir">Avinash Sir</option>
              </Form.Select>
            </Form.Group> */}
            <Modal.Footer>
              <Button
                variant="danger"
                className="rounded-pill px-4"
                onClick={closeAddModal}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="success"
                className="rounded-pill px-4"
                type="submit"
                disabled={loading}
              >
                {loading ? 'Saving…' : 'Save Institute'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>
      <EditInstituteModal
        show={showEditModal}
        handleClose={() => setShowEditModal(false)}
        institute={selectedInstitute}
      />
      </div>
    </>
  )
}

export default ManageInstitute
